import { Children, type ReactNode } from 'react';
import { Card } from './Card';
import { SectionLabel } from './SectionLabel';
import { cn } from './utils';

/**
 * Page-grammar primitive (SIL-538, W0 of
 * resources/plans/wayfinder-ux-consistency-spec.md §1.1-1.2): the stat-tile
 * row that sits between `PageHeader` and the first `Card` panel on a report
 * page. DESIGN.md's stat-tile-grid exemption (amended SIL-541) permits 3-4
 * tiles across; **four is the default, three is permitted, five or more is
 * never** — this component enforces that rather than leaving it to each
 * call site to remember.
 */

const COL_CLASS: Record<3 | 4, string> = {
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 xl:grid-cols-4',
};

export function MetricGrid({ children, className }: { children: ReactNode; className?: string }) {
  const count = Children.count(children);
  if (count < 3 || count > 4) {
    // Dev-time guardrail, not a thrown error — a grid that's briefly wrong
    // while a route is mid-edit shouldn't crash the page.
    // eslint-disable-next-line no-console
    console.warn(
      `MetricGrid: expected 3-4 StatTile children (DESIGN.md's four-tile default / three-permitted / five-or-more-never rule), got ${count}.`,
    );
  }
  return (
    <div className={cn('grid grid-cols-1 gap-3.5', COL_CLASS[count === 3 ? 3 : 4], className)}>
      {children}
    </div>
  );
}

export type StatTileTone = 'good' | 'neutral' | 'blue' | 'warn';

/**
 * Semantic tokens only, and every role already exists elsewhere in the
 * system — no new colour is introduced for this primitive:
 *   - `good` -> `--status-green` (StatusPill's own "positive" tone)
 *   - `warn` -> `--status-amber` (StatusPill's own "fallback/moderate" tone)
 *   - `blue` -> `--color-primary` (the portal's own brand accent — ops
 *     violet, customer emerald — used here as "informational", not "act")
 *   - `neutral` -> `--muted-foreground` (StatusPill's own non-verdict tone)
 *
 * The tone colors the note/indicator dot only, never the headline value —
 * DESIGN.md's One Voice Rule keeps a static figure off emerald/brand color;
 * this primitive respects that by construction rather than by convention.
 */
const TONE_DOT: Record<StatTileTone, string> = {
  good: 'bg-status-green',
  warn: 'bg-status-amber',
  blue: 'bg-primary',
  neutral: 'bg-muted-foreground',
};

const TONE_TEXT: Record<StatTileTone, string> = {
  good: 'text-status-green',
  warn: 'text-status-amber',
  blue: 'text-primary',
  neutral: 'text-muted-foreground',
};

export function StatTile({
  label,
  value,
  note,
  tone = 'neutral',
  className,
}: {
  label: string;
  value: string;
  /** Small qualifying line under the value, e.g. "+4.2% this week". */
  note?: ReactNode;
  tone?: StatTileTone;
  className?: string;
}) {
  return (
    <Card padding="md" className={className}>
      <SectionLabel>{label}</SectionLabel>
      <p className="mt-2 truncate font-num text-2xl font-semibold text-foreground">{value}</p>
      {note && (
        <p className={cn('mt-1 flex items-center gap-1.5 text-xs', TONE_TEXT[tone])}>
          <span className={cn('size-1.5 shrink-0 rounded-full', TONE_DOT[tone])} />
          <span className="truncate">{note}</span>
        </p>
      )}
    </Card>
  );
}
